"use client";

import clsx from "clsx";
import React from "react";
import { Link } from "@repo/next-utils/router";
import { Box } from "@repo/ui/box";
import { Root as Container } from "@repo/ui/container";
import * as Flex from "@repo/ui/flex";
import { useFloorContext } from "@/components/floor-context/floor-context";
import {
  TOPSHOP_INSTAGRAM_URL,
  TOPMAN_INSTAGRAM_URL,
  TIKTOK_URL,
  PINTEREST_URL,
  CORPORATE_RESPONSIBILITY_URL,
  STRINGS,
} from "@/constants";
import {
  TYPOGRAPHY_HEADING3_BOLD,
  TYPOGRAPHY_LABEL3,
  TYPOGRAPHY_BODY2,
  TYPOGRAPHY_BODY3_SENTENCE,
} from "@/theme/topshop/typography/typography-constants";
import { Logo } from "../logo";
import * as Viewport from "../viewport";
import classes from "./footer.module.css";

const INSTAGRAM_URLS = {
  topshop: TOPSHOP_INSTAGRAM_URL,
  topman: TOPMAN_INSTAGRAM_URL,
} as const;

export const Footer = () => {
  const { floor } = useFloorContext();

  const instagramUrl = floor ? INSTAGRAM_URLS[floor] : TOPSHOP_INSTAGRAM_URL;

  return (
    <>
      <Viewport.Large>
        <footer className={classes.footerContainer} role="contentinfo">
          <Container size="large" gutter="2xl">
            <Flex.Container
              gap="xl"
              justify="start"
              className={classes.mainContent}
              asChild
            >
              <nav aria-label={STRINGS.footer.ariaLabels.footerNavigation}>
                <Flex.Container column gap="xs" asChild>
                  <Flex.Item asChild>
                    <section aria-labelledby="brand-heading">
                      <h3 className={TYPOGRAPHY_HEADING3_BOLD}>
                        {STRINGS.footer.headings.brand}
                      </h3>
                      <Flex.Container column gap="2xs" asChild>
                        <ul>
                          <li>
                            <Link
                              href="/about"
                              className={clsx(
                                TYPOGRAPHY_LABEL3,
                                classes.siteMapLink,
                              )}
                            >
                              {STRINGS.footer.links.about}
                            </Link>
                          </li>
                          <li>
                            <Link
                              href="/press"
                              className={clsx(
                                TYPOGRAPHY_LABEL3,
                                classes.siteMapLink,
                              )}
                            >
                              {STRINGS.footer.links.press}
                            </Link>
                          </li>
                        </ul>
                      </Flex.Container>
                    </section>
                  </Flex.Item>
                </Flex.Container>

                <Flex.Container column gap="xs" asChild>
                  <Flex.Item asChild>
                    <section aria-labelledby="social-heading">
                      <h3 className={TYPOGRAPHY_HEADING3_BOLD}>
                        {STRINGS.footer.headings.social}
                      </h3>
                      <Flex.Container column gap="2xs" asChild>
                        <ul>
                          <li>
                            <a
                              href={instagramUrl}
                              className={clsx(
                                TYPOGRAPHY_LABEL3,
                                classes.siteMapLink,
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={
                                STRINGS.footer.ariaLabels.instagramNewTab
                              }
                              suppressHydrationWarning={true}
                            >
                              {STRINGS.footer.links.instagram}
                            </a>
                          </li>
                          <li>
                            <a
                              href={TIKTOK_URL}
                              className={clsx(
                                TYPOGRAPHY_LABEL3,
                                classes.siteMapLink,
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={
                                STRINGS.footer.ariaLabels.tiktokNewTab
                              }
                            >
                              {STRINGS.footer.links.tiktok}
                            </a>
                          </li>
                          <li>
                            <a
                              href={PINTEREST_URL}
                              className={clsx(
                                TYPOGRAPHY_LABEL3,
                                classes.siteMapLink,
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={
                                STRINGS.footer.ariaLabels.pinterestNewTab
                              }
                            >
                              {STRINGS.footer.links.pinterest}
                            </a>
                          </li>
                        </ul>
                      </Flex.Container>
                    </section>
                  </Flex.Item>
                </Flex.Container>

                <Flex.Container column gap="xs" asChild>
                  <Flex.Item asChild>
                    <section aria-labelledby="help-heading">
                      <h3 className={TYPOGRAPHY_HEADING3_BOLD}>
                        {STRINGS.footer.headings.help}
                      </h3>
                      <Flex.Container column gap="2xs" asChild>
                        <ul>
                          <li>
                            <Link
                              href="/delivery-and-returns"
                              className={clsx(
                                TYPOGRAPHY_LABEL3,
                                classes.siteMapLink,
                              )}
                            >
                              {STRINGS.footer.links.deliveryAndReturns}
                            </Link>
                          </li>
                          <li>
                            <Link
                              href="/customer-care"
                              className={clsx(
                                TYPOGRAPHY_LABEL3,
                                classes.siteMapLink,
                              )}
                            >
                              {STRINGS.footer.links.customerCare}
                            </Link>
                          </li>
                        </ul>
                      </Flex.Container>
                    </section>
                  </Flex.Item>
                </Flex.Container>

                <Flex.Container column gap="xs" asChild>
                  <Flex.Item asChild>
                    <section aria-labelledby="policies-heading">
                      <h3 className={TYPOGRAPHY_HEADING3_BOLD}>
                        {STRINGS.footer.headings.policies}
                      </h3>
                      <Flex.Container column gap="2xs" asChild>
                        <ul>
                          <li>
                            <Link
                              href="/content/data-protection"
                              className={clsx(
                                TYPOGRAPHY_LABEL3,
                                classes.siteMapLink,
                              )}
                              target="_blank"
                            >
                              {STRINGS.footer.links.dataProtection}
                            </Link>
                          </li>
                          <li>
                            <Link
                              href="/content/compliance"
                              className={clsx(
                                TYPOGRAPHY_LABEL3,
                                classes.siteMapLink,
                              )}
                              target="_blank"
                            >
                              {STRINGS.footer.links.compliance}
                            </Link>
                          </li>
                          <li>
                            <Link
                              href="/content/cyber-security"
                              className={clsx(
                                TYPOGRAPHY_LABEL3,
                                classes.siteMapLink,
                              )}
                              target="_blank"
                            >
                              {STRINGS.footer.links.cybersecurity}
                            </Link>
                          </li>
                          <li>
                            <a
                              href={CORPORATE_RESPONSIBILITY_URL}
                              className={clsx(
                                TYPOGRAPHY_LABEL3,
                                classes.siteMapLink,
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={
                                STRINGS.footer.ariaLabels
                                  .corporateResponsibilityNewTab
                              }
                            >
                              {STRINGS.footer.links.corporateResponsibility}
                            </a>
                          </li>
                        </ul>
                      </Flex.Container>
                    </section>
                  </Flex.Item>
                </Flex.Container>
              </nav>
            </Flex.Container>

            <Box paddingY="3xl" asChild>
              <Flex.Container
                justify="space-between"
                aria-hidden="true"
                columnGap="3xl"
              >
                <Logo variant="topshop" />
                <Logo variant="topman" />
              </Flex.Container>
            </Box>
          </Container>

          <Box
            backgroundColor="secondary"
            className={classes.bottomFooter}
            paddingX="3xl"
            asChild
          >
            <Flex.Container justify="space-between" align="center">
              <p className={TYPOGRAPHY_BODY3_SENTENCE}>
                &copy; {new Date().getFullYear()} {STRINGS.footer.copyright}
              </p>
              <Flex.Container align="center" gap="xs">
                <Link
                  href="/content/privacy-policy"
                  target="_blank"
                  className={`${classes.link} ${TYPOGRAPHY_LABEL3}`}
                  aria-label={STRINGS.footer.ariaLabels.privacyPolicyNewTab}
                >
                  {STRINGS.footer.links.privacyAndCookies}
                </Link>
                <span className={classes.divider} />
                <Link
                  href="/terms-and-conditions"
                  target="_blank"
                  className={`${classes.link} ${TYPOGRAPHY_BODY2}`}
                  aria-label={
                    STRINGS.footer.ariaLabels.termsAndConditionsNewTab
                  }
                >
                  {STRINGS.footer.links.termsAndConditions}
                </Link>
                <span className={classes.divider} />
                <Link
                  href="/content/accessibility"
                  target="_blank"
                  className={`${classes.link} ${TYPOGRAPHY_BODY2}`}
                  aria-label={STRINGS.footer.ariaLabels.accessibilityNewTab}
                >
                  {STRINGS.footer.links.accessibility}
                </Link>
              </Flex.Container>
            </Flex.Container>
          </Box>
        </footer>
      </Viewport.Large>

      <Viewport.Small>
        <footer className={classes.viewportSmall} role="contentinfo">
          <Box paddingX="lg" asChild>
            <Flex.Container
              column
              gap="lg"
              className={classes.smallViewportLayout}
            >
              <Box paddingY="3xl" asChild>
                <Flex.Container
                  column
                  align="center"
                  justify="space-between"
                  gap="md"
                >
                  <Logo variant="topshop" width="250px" height="36px" />
                  <Logo variant="topman" width="225px" height="36px" />
                </Flex.Container>
              </Box>
            </Flex.Container>
          </Box>

          <Box
            backgroundColor="secondary"
            paddingY="xs"
            paddingX="xl"
            className={classes.bottomFooter}
            asChild
          >
            <Flex.Container align="center" gap="md" justify="space-between">
              <p className={TYPOGRAPHY_BODY3_SENTENCE}>
                &copy; {new Date().getFullYear()}{" "}
                {STRINGS.footer.copyrightMobile}
              </p>
              <Flex.Container align="center" wrap gap="xs">
                <Link
                  href="/content/privacy-policy"
                  target="_blank"
                  className={TYPOGRAPHY_BODY3_SENTENCE}
                  aria-label={STRINGS.footer.ariaLabels.privacyPolicyNewTab}
                >
                  {STRINGS.footer.links.privacyAndCookies}
                </Link>
                <span className={classes.divider} />
                <Link
                  href="/terms-and-conditions"
                  target="_blank"
                  className={TYPOGRAPHY_BODY3_SENTENCE}
                  aria-label={
                    STRINGS.footer.ariaLabels.termsAndConditionsNewTab
                  }
                >
                  {STRINGS.footer.links.termsAndConditionsShort}
                </Link>
              </Flex.Container>
            </Flex.Container>
          </Box>
        </footer>
      </Viewport.Small>
    </>
  );
};
